import { SENDA } from '@somnhold/content';
import { cosA, dist2, iatan2, idiv, imax, imin, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, GuardRules, Hero, SendaNpc, SeventhRules, SeventhState } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { guardTick } from './keep.ts';
import { ISLAND_R, quarterOf, radiusOf, wetR, type CisternLayout, type YardLayout } from './seventh-layout.ts';

/**
 * The seventh altar (docs/sendas/paladin.md, chapter 5; §4, «El séptimo»; §5, the path's enemy), on two boards
 * (battle/seventh-layout.ts):
 *
 * - The rubble (`rubble`, act 1): the stair under the keep, under the fall's rubble. Each heap is cleared standing still
 *   by it a while (Marit and Nuño carry what the hero lifts); the old garrison's skeletons come up the holes in bands,
 *   and a bone singer now and then. Won a moment after the last heap falls (the stair older than the tower shows).
 * - The well (`well`, act 3, the test): the cistern's marks, the island bare at `bare` or fewer and the ring the fight
 *   needs at `ring` or more. Each of the four gates, turned, drains its marks over a pulse (turned again while it runs,
 *   it runs another); a spring adds one now and then. Won with the gates still and the water between the two, a moment
 *   settled. The squires (Marit's nightmare's own, small) rise out of the water for her: seen only in water or on salt,
 *   so the lower the water the longer they walk unseen.
 * - La Que Huyó (`fled`, act 4): she goes for Marit, always straight: a line marked on the floor a while, then the run;
 *   the hero on it takes the blow (through armour and shields; devotion counts), else Marit does. Near the hero she
 *   slashes. She hides (seen only in the water and on the salt runnels) now and then; below two thirds she strikes the
 *   gates open and the ring drains, and hides for good (the hero shuts the gates one by one, she goes to open them
 *   again; salted runnels show her); below a third she can't hide (trying, she stands still and takes double) but runs
 *   from two places, her reflection in the water running too. The oath's bar rises (by itself with Marit praying, with
 *   the hero by the altar if the hero swears); full, she loses half of what she has left and stops hiding. Down with the
 *   bar unfilled, it fills in the calm. The salt folk salt where she hides, Varo's knights hold a gate, Varo takes one
 *   line for Marit.
 *
 * Cost: one boss and a handful of squires; the water a number per quarter; the runnels a short list; a line is two
 * points (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

/** What the hero can be sent to (the `mark` command's `at`): a heap, a gate, a runnel. */
export const SEV_PILE = 0;
export const SEV_GATE = 10;
export const SEV_RUNNEL = 20;
/** Never: a time no fight reaches. */
const NEVER = 0x3fffffff;

type Pt = { x: number; y: number };

/** The rules of each act (the fight's builder, campaign/sendas.ts; and the renderer's scenes). */
export const seventhRules = (
  kind: SeventhRules['kind'],
  yard: YardLayout,
  cist: CisternLayout,
  marit: GuardRules,
  shieldHp: number,
  boss: { hp: number; together: boolean; ring: number },
): SeventhRules => {
  const S = SENDA.seventh;
  const W = S.well;
  const shield = { hp: shieldHp, first: W.shield.first, every: W.shield.every, n: W.shield.n, maritBp: W.shield.maritBp, hitEvery: W.shield.hitEvery, blows: W.shield.blows, pace: W.shield.pace, reach: W.shield.reach };
  if (kind === 'rubble') {
    const R = S.rubble;
    const keeps = Object.entries(yard.keeps).map(([id, p]) => ({ id, x: p.x, y: p.y }));
    return { kind, keeps, marit, shield, rubble: { piles: yard.piles.map((p) => ({ ...p })), stand: R.stand, reach: R.reach, holes: yard.holes.map((p) => ({ ...p })), first: R.first, every: R.every, n: R.n, singer: R.singer.slice(), settle: 36 } };
  }
  const K = cist.keeps as Record<string, Pt>;
  const well = { cap: W.cap, start: W.start, bare: W.bare, ring: W.ring, take: W.take.slice(), pulse: W.pulse, turn: W.turn, reach: W.reach, spring: W.spring, gates: cist.gates.map((g) => ({ ...g })), settle: 24 };
  if (kind === 'well') {
    return { kind, keeps: [{ id: 'marit', x: K.marit!.x, y: K.marit!.y }], marit, shield, well };
  }
  const F = S.fled;
  const m = boss.together ? K.maritAltar! : K.maritAway!;
  const g = cist.gates[cist.held] as Pt;
  // The knights at their gate, a little out on the rim (they hold it from behind).
  const out = (p: Pt, d: number, side: number): Pt => {
    const r = imax(1, radiusOf(p.x, p.y));
    return { x: p.x + idiv(p.x * d, r) + idiv(-p.y * side, r), y: p.y + idiv(p.y * d, r) + idiv(p.x * side, r) };
  };
  const k1 = out(g, 700, 500);
  const k2 = out(g, 700, -500);
  return {
    kind,
    keeps: [
      { id: 'marit', x: m.x, y: m.y },
      { id: 'nuno', x: K.nuno!.x, y: K.nuno!.y },
      { id: 'orderKnight', x: k1.x, y: k1.y },
      { id: 'orderKnight2', x: k2.x, y: k2.y },
      { id: 'salinera', x: K.salinera!.x, y: K.salinera!.y },
      { id: 'salter', x: K.salter!.x, y: K.salter!.y },
      { id: 'varo', x: K.varo!.x, y: K.varo!.y },
    ],
    marit,
    shield: { ...shield, first: F.shield.first, every: F.shield.every, n: F.shield.n, maritBp: boss.together ? mulBp(F.shield.maritBp, F.kneelBp) : F.shield.maritBp, blows: F.shield.blows },
    well: { ...well, start: boss.ring, cap: boss.ring },
    fled: {
      hp: boss.hp,
      together: boss.together,
      ring: boss.ring,
      altar: { x: 0, y: 0 },
      chargeBp: F.chargeBp,
      maritBp: boss.together ? mulBp(F.maritBp, F.kneelBp) : F.maritBp,
      reflectBp: F.reflectBp,
      warn: F.warn,
      every: F.every.slice(),
      width: F.width,
      dash: F.dash,
      linger: F.linger,
      pace: F.pace,
      slash: { ...F.slash },
      hide: { ...F.hide },
      still: { ...F.still },
      oath: F.oath,
      near: F.near,
      calm: F.calm,
      salt: { ...F.salt },
      gate: { ...F.gate },
      runnels: cist.runnels.map((x) => x.slice()),
      held: cist.held,
      shield: { ...F.shield },
      varoAt: F.varoAt,
      varoIn: { x: K.varoIn!.x, y: K.varoIn!.y },
      varoOut: { x: K.varo!.x, y: K.varo!.y },
    },
  };
};

/** Each act as it starts (the renderer's scenes build one too). */
export const seventhFresh = (r: SeventhRules, npcs = 0): SeventhState => ({
  swing: new Array<number>(npcs).fill(0),
  frac: 0,
  job: -1,
  held: 0,
  cleared: new Array<number>(r.rubble?.piles.length ?? 0).fill(0),
  pile: -1,
  work: 0,
  band: 0,
  doneAt: -1,
  level: r.well?.start ?? 0,
  acc: 0,
  open: [0, 0, 0, 0],
  into: [0, 0, 0, 0],
  took: [0, 0, 0, 0],
  calmT: 0,
  shieldId: [],
  shieldHits: [],
  shieldAt: r.shield.first,
  boss: 0,
  phase: 0,
  act: 0,
  until: 0,
  tx: 0,
  ty: -4000,
  next: 0,
  line: [],
  echo: [],
  hidden: 0,
  hideAt: 0,
  quarter: r.fled ? [r.fled.ring, r.fled.ring, r.fled.ring, r.fled.ring] : [0, 0, 0, 0],
  gate: [0, 0, 0, 0],
  reopen: [0, 0, 0, 0],
  salt: new Array<number>(r.fled?.runnels.length ?? 0).fill(0),
  maritSalt: 0,
  folkSalt: 0,
  oath: 0,
  caught: 0,
  fell: -1,
  varo: 0,
  covered: 0,
  struck: 0,
  marks: [],
  done: false,
});

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const keepOf = (r: SeventhRules, id: string): Pt | undefined => r.keeps.find((k) => k.id === id);

/** Index of a sorted list's value (−1: not in it). */
const find = (list: readonly number[], v: number): number => {
  let lo = 0;
  let hi = list.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = list[m] as number;
    if (x === v) return m;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return -1;
};

/** A blow on a person in bp of its life, its fractions kept (the squires' 6 %, the run's 12 %). */
const hurt = (s: BattleState, w: SeventhState, n: SendaNpc, bp: number): void => {
  if (n.hp <= 0) return;
  w.frac += n.maxHp * bp;
  let k = 0;
  while (w.frac >= 10000) {
    w.frac -= 10000;
    k++;
  }
  if (!k) return;
  n.hp -= k;
  ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0) {
    n.hp = 0;
    ev(s, { t: 'senda', kind: 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/* ---------- the water ---------- */

/** Whether a point stands in the water now: the cistern's (act 3: anywhere under its marks; act 4: its quarter's ring). */
export const sevWet = (r: SeventhRules, w: SeventhState, x: number, y: number): boolean => {
  const rr = radiusOf(x, y);
  if (r.kind === 'well') return rr < wetR(w.level) && (w.level > r.well!.bare || rr > ISLAND_R);
  if (r.kind !== 'fled') return false;
  const q = w.quarter[quarterOf(x, y)] ?? 0;
  return q > 0 && rr > ISLAND_R && rr < wetR(q);
};

/** The distance² from a point to a segment (x0, y0, x1, y1), and how far along it (thousandths). */
export const segDist2 = (px: number, py: number, seg: readonly number[]): { d2: number; t: number } => {
  const [ax, ay, bx, by] = seg as [number, number, number, number];
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  t = imax(0, imin(1000, t));
  const cx = ax + idiv(dx * t, 1000);
  const cy = ay + idiv(dy * t, 1000);
  return { d2: dist2(px, py, cx, cy), t };
};

/** Whether a point is on a salted runnel (−1: none; else its index). */
const onSalt = (r: NonNullable<SeventhRules['fled']>, w: SeventhState, x: number, y: number): number => {
  for (let i = 0; i < r.runnels.length; i++) {
    if (!w.salt[i]) continue;
    if (segDist2(x, y, r.runnels[i] as number[]).d2 <= r.salt.width * r.salt.width) return i;
  }
  return -1;
};

/** Whether this nightmare is the act's own (La Que Huyó, a squire): its blows on people are counted here, not by the path's base. */
export const sevOwnBlow = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.seventh;
  return !!w && (e.id === w.boss || find(w.shieldId, e.id) >= 0);
};

/** Whether a nightmare shows (combat's `isRevealed`): the squires only in water or on salt; La Que Huyó so while she hides. */
export const sevSeen = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.seventh;
  const w = s.senda?.seventh;
  if (!r || !w) return true;
  const own = e.id === w.boss;
  if (!own && find(w.shieldId, e.id) < 0) return true;
  if (own && (!(w.hidden > s.tick) || w.caught > s.tick)) return true;
  if (sevWet(r, w, e.x, e.y)) return true;
  return !!r.fled && onSalt(r.fled, w, e.x, e.y) >= 0;
};

/** La Que Huyó's blows while she stands still (trying to hide below a third): double. */
export const sevTakes = (s: BattleState, e: Enemy, dmg: number): number => {
  const r = s.rules?.senda?.seventh?.fled;
  const w = s.senda?.seventh;
  return r && w && e.id === w.boss && w.act === 4 ? mulBp(dmg, r.still.takenBp) : dmg;
};

/* ---------- the hero's jobs ---------- */

/** Where a job is: a heap, a gate, a runnel's middle. */
export const sevJobAt = (r: SeventhRules, at: number): Pt | null => {
  if (r.rubble && at >= SEV_PILE && at < SEV_PILE + r.rubble.piles.length) return r.rubble.piles[at - SEV_PILE] as Pt;
  if (r.well && at >= SEV_GATE && at < SEV_GATE + r.well.gates.length) return r.well.gates[at - SEV_GATE] as Pt;
  if (r.fled && at >= SEV_RUNNEL && at < SEV_RUNNEL + r.fled.runnels.length) {
    const k = r.fled.runnels[at - SEV_RUNNEL] as number[];
    return { x: idiv((k[0] as number) + (k[2] as number), 2), y: idiv((k[1] as number) + (k[3] as number), 2) };
  }
  return null;
};

/** The hero sent to a job (the `mark` command). */
export const sevOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.seventh;
  const w = s.senda?.seventh;
  if (!r || !w) return;
  const p = sevJobAt(r, at);
  if (!p) return;
  w.job = at;
  w.held = 0;
  // (A gate from the rim's side: the basin is deep in the test.)
  const rr = imax(1, radiusOf(p.x, p.y));
  const q = at >= SEV_GATE && at < SEV_RUNNEL ? { x: p.x + idiv(p.x * 500, rr), y: p.y + idiv(p.y * 500, rr) } : p;
  orderMove(s, h, q.x, q.y);
  h.target = 0;
  h.forced = false;
};

/** A gate turned (the test: one more pulse; the fight: shut or opened). */
const turnGate = (s: BattleState, r: SeventhRules, w: SeventhState, g: number, cue: (c: string) => void): void => {
  const W = r.well as NonNullable<SeventhRules['well']>;
  const p = W.gates[g] as Pt;
  if (r.kind === 'well') {
    w.open[g] = (w.open[g] as number) + 1;
    ev(s, { t: 'senda', kind: 'gate', npc: '', x: p.x, y: p.y, n: g * 2 + 1 });
    cue(`gate${g}`);
    return;
  }
  const F = r.fled as NonNullable<SeventhRules['fled']>;
  w.gate[g] = w.gate[g] ? 0 : 1;
  ev(s, { t: 'senda', kind: 'gate', npc: '', x: p.x, y: p.y, n: g * 2 + (w.gate[g] as number) });
  // Shut while she hides: she goes to open it again.
  if (!w.gate[g] && w.phase === 1 && w.oath < F.oath) w.reopen[g] = s.tick + F.gate.reopen;
  else w.reopen[g] = 0;
  cue(w.gate[g] ? 'opened' : 'shut');
};

const work = (s: BattleState, r: SeventhRules, w: SeventhState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const p = sevJobAt(r, w.job);
  if (!p) {
    w.job = -1;
    return;
  }
  const gate = w.job >= SEV_GATE && w.job < SEV_RUNNEL;
  const reach = r.rubble ? r.rubble.reach : gate ? (r.fled ? r.fled.gate.reach : (r.well as NonNullable<SeventhRules['well']>).reach) + 500 : (r.fled as NonNullable<SeventhRules['fled']>).salt.reach;
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, reach)) {
    w.held = 0;
    if (!h.moving && !withinRange(h.x, h.y, p.x, p.y, reach)) w.job = -1;
    return;
  }
  // (The rubble's heaps are worked by standing at them, job or not: rubbleTick.)
  if (r.rubble) return;
  const need = gate ? (r.fled ? r.fled.gate.close : (r.well as NonNullable<SeventhRules['well']>).turn) : (r.fled as NonNullable<SeventhRules['fled']>).salt.stand;
  if (++w.held < need) return;
  const j = w.job;
  w.job = -1;
  w.held = 0;
  if (gate) {
    turnGate(s, r, w, j - SEV_GATE, cue);
    return;
  }
  const k = j - SEV_RUNNEL;
  if (w.salt[k]) return;
  w.salt[k] = s.tick;
  ev(s, { t: 'senda', kind: 'salt', npc: '', x: p.x, y: p.y, n: k });
  cue('salt');
};

/* ---------- act 1: the rubble ---------- */

const rubbleTick = (s: BattleState, r: SeventhRules, w: SeventhState, cue: (c: string) => void): void => {
  const R = r.rubble as NonNullable<SeventhRules['rubble']>;
  const T = s.waveT;
  // The garrison up the holes: a band at two holes in turn; a bone singer now and then.
  if (T >= R.first + w.band * R.every && w.doneAt < 0) {
    // (A band of `n`, or one more: the seed's.)
    const n = R.n + rngInt(s.rng, 2);
    for (let k = 0; k < n; k++) {
      const hole = R.holes[(w.band * 2 + (k & 1)) % R.holes.length] as Pt;
      const e = spawnEnemy(s, 'skeleton', 1, false, 0, hole.x + (k >> 1) * 300, hole.y + (k & 1) * 200);
      if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
      e.anim = 'walk';
    }
    for (const at of R.singer)
      if (at >= R.first + w.band * R.every && at < R.first + (w.band + 1) * R.every) {
        const hole = R.holes[(w.band + 1) % R.holes.length] as Pt;
        const e = spawnEnemy(s, 'bonesinger', 1, false, 0, hole.x, hole.y);
        if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
        cue('singer');
      }
    w.band++;
  }
  if (w.doneAt >= 0) return;
  // A heap: standing by the nearest one left (striking from there counts; walking off leaves what was done).
  const h = s.heroes.find((x) => x.downTicks === 0);
  let best = -1;
  let bd = 0x7fffffff;
  if (h)
    for (let i = 0; i < R.piles.length; i++) {
      if ((w.cleared[i] as number) >= R.stand) continue;
      const p = R.piles[i] as Pt;
      const d = dist2(h.x, h.y, p.x, p.y);
      if (d <= R.reach * R.reach && d < bd) {
        bd = d;
        best = i;
      }
    }
  w.pile = best;
  if (best < 0) return;
  if (!w.cleared[best]) cue('lift');
  w.cleared[best] = (w.cleared[best] as number) + 1;
  w.work = w.cleared[best] as number;
  if ((w.cleared[best] as number) < R.stand) return;
  w.pile = -1;
  w.work = 0;
  if (w.job === SEV_PILE + best) w.job = -1;
  const p = R.piles[best] as Pt;
  const n = sevCleared(r, w);
  ev(s, { t: 'senda', kind: 'pile', npc: '', x: p.x, y: p.y, n: best });
  cue(`pile${n}`);
  if (n >= R.piles.length) {
    w.doneAt = s.tick;
    ev(s, { t: 'senda', kind: 'stair', npc: '', x: 0, y: -3300 });
    cue('stair');
  }
};

/** The rubble's heaps cleared. */
export const sevCleared = (r: SeventhRules, w: SeventhState): number => {
  const R = r.rubble;
  if (!R) return 0;
  let n = 0;
  for (const c of w.cleared) if (c >= R.stand) n++;
  return n;
};

/* ---------- the squires ---------- */

/**
 * A squire out of the water, on Marit's side (the test: a way off the water's edge, so it swims a while in sight; the
 * fight: in the ring's wettest quarter nearest her, its middle).
 */
const surface = (s: BattleState, r: SeventhRules, w: SeventhState, near: Pt): void => {
  let at = iatan2(near.y, near.x);
  let edge = imax(ISLAND_R + 400, wetR(imin(5, w.level)) - 2600);
  if (r.kind === 'fled') {
    let best = -1;
    for (let k = 0; k < 4; k++) if (best < 0 || (w.quarter[k] as number) > (w.quarter[best] as number) || ((w.quarter[k] as number) === (w.quarter[best] as number) && k === quarterOf(near.x, near.y))) best = k;
    // The quarters' middles: SW, NW, NE, SE.
    at = [384, 640, 896, 128][best] as number;
    edge = imax(ISLAND_R + 500, idiv(ISLAND_R + wetR(imax(1, w.quarter[best] as number)), 2));
  }
  for (let k = 0; k < r.shield.n; k++) {
    const a = at + rngRange(s.rng, -90, 90);
    const x = idiv(cosA(a) * edge, 10000);
    const y = idiv(sinA(a) * edge, 10000);
    const e = spawnEnemy(s, 'goblin', 1, false, 0, x, y, r.shield.hp);
    e.facing = iatan2(near.y - y, near.x - x);
    e.anim = 'walk';
    let j = 0;
    while (j < w.shieldId.length && (w.shieldId[j] as number) < e.id) j++;
    w.shieldId.splice(j, 0, e.id);
    w.shieldHits.splice(j, 0, 0);
    ev(s, { t: 'senda', kind: 'surface', npc: '', x, y });
  }
};

/** The squires: out every so often, for Marit; at her they strike her. */
const shieldsTick = (s: BattleState, r: SeventhRules, w: SeventhState, on: boolean, cue: (c: string) => void): void => {
  const marit = npcOf(s, 'marit');
  if (on && marit && marit.hp > 0 && s.waveT >= w.shieldAt) {
    w.shieldAt = s.waveT + r.shield.every;
    surface(s, r, w, marit);
    cue('squires');
  }
  if (s.tick % 24 === 0)
    for (let i = w.shieldId.length - 1; i >= 0; i--) {
      const id = w.shieldId[i] as number;
      if (s.enemies.some((e) => e.id === id && e.hp > 0)) continue;
      w.shieldId.splice(i, 1);
      w.shieldHits.splice(i, 1);
    }
};

/** A squire's own step: to Marit, and at her a few blows, every so often; then it comes apart. */
const squireMove = (s: BattleState, r: SeventhRules, w: SeventhState, e: Enemy, i: number): void => {
  const marit = npcOf(s, 'marit');
  if (!marit || marit.hp <= 0) {
    e.anim = 'idle';
    return;
  }
  if (withinRange(e.x, e.y, marit.x, marit.y, r.shield.reach)) {
    e.facing = iatan2(marit.y - e.y, marit.x - e.x);
    if (e.cd > 0) e.cd--;
    else {
      e.cd = r.shield.hitEvery;
      e.anim = 'attack';
      e.animT = 0;
      hurt(s, w, marit, r.shield.maritBp);
      w.shieldHits[i] = (w.shieldHits[i] as number) + 1;
      if ((w.shieldHits[i] as number) >= r.shield.blows) {
        e.hp = 0;
        ev(s, { t: 'senda', kind: 'fade', npc: '', x: e.x, y: e.y });
      }
    }
    return;
  }
  e.anim = 'walk';
  stepToward(e, marit.x, marit.y, r.shield.pace);
};

/* ---------- act 3: the cistern's gates ---------- */

const wellTick = (s: BattleState, r: SeventhRules, w: SeventhState, cue: (c: string) => void): void => {
  const W = r.well as NonNullable<SeventhRules['well']>;
  // The spring under the cistern (its marks go out with the gates while one runs: what is taken is what goes).
  const flowing = w.open.some((x) => x > 0);
  if (!flowing && ++w.acc >= W.spring) {
    w.acc = 0;
    if (w.level < W.cap) {
      w.level++;
      ev(s, { t: 'senda', kind: 'spring', npc: '', x: 0, y: 0, n: w.level });
    }
  }
  // Each gate's pulse: its marks out a little at a time while it runs.
  let running = false;
  for (let g = 0; g < W.gates.length; g++) {
    if (!w.open[g]) continue;
    running = true;
    w.into[g] = (w.into[g] as number) + 1;
    const want = idiv((W.take[g] as number) * (w.into[g] as number), W.pulse);
    while ((w.took[g] as number) < want && w.level > 0) {
      w.level--;
      w.took[g] = (w.took[g] as number) + 1;
      const p = W.gates[g] as Pt;
      ev(s, { t: 'senda', kind: 'pour', npc: '', x: p.x, y: p.y, n: g });
      ev(s, { t: 'senda', kind: 'mark', npc: '', x: 0, y: 0, n: w.level });
    }
    if ((w.into[g] as number) >= W.pulse) {
      w.open[g] = (w.open[g] as number) - 1;
      w.into[g] = 0;
      w.took[g] = 0;
      if (!w.open[g]) {
        const p = W.gates[g] as Pt;
        ev(s, { t: 'senda', kind: 'gate', npc: '', x: p.x, y: p.y, n: g * 2 });
      }
    }
  }
  // The island in the air with the ring kept, the water still a moment: done.
  const right = !running && w.level <= W.bare && w.level >= W.ring;
  if (right) {
    if (w.calmT === 0) cue('bare');
    if (++w.calmT >= W.settle) w.done = true;
  } else w.calmT = 0;
  if (!running && w.level < W.ring) cue('low');
  shieldsTick(s, r, w, !w.done, cue);
  // Marit at the stair's foot: she strikes what she sees.
  const marit = npcOf(s, 'marit');
  if (marit && s.senda) {
    const i = s.senda.npcs.indexOf(marit);
    w.swing[i] = guardTick(s, marit, keepOf(r, 'marit') as Pt, r.marit, w.swing[i] ?? 0, (e) => !sevSeen(s, e));
  }
};

/* ---------- act 4: La Que Huyó ---------- */

/** Her life in bp of her whole. */
const lifeBp = (e: Enemy | undefined): number => (e && e.maxHp > 0 ? idiv(imax(0, e.hp) * 10000, e.maxHp) : 0);

/** A run's line: from her to a hex short of Marit. */
const lineTo = (from: Pt, to: Pt): number[] => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d = imax(1, radiusOf(dx, dy));
  const back = imin(900, d - 100);
  return [from.x, from.y, to.x - idiv(dx * back, d), to.y - idiv(dy * back, d)];
};

/** Whether the standing hero is on a line (within its width, not behind where she starts). */
export const sevOnLine = (r: NonNullable<SeventhRules['fled']>, h: Pt, seg: readonly number[]): boolean => {
  if (seg.length < 4) return false;
  const { d2, t } = segDist2(h.x, h.y, seg);
  return t > 30 && d2 <= r.width * r.width;
};

/** The quarter's water toward what its gate says: shut, the ring; open, nothing. */
const waterTick = (s: BattleState, F: NonNullable<SeventhRules['fled']>, w: SeventhState): void => {
  const step = imax(1, idiv(F.gate.drain, imax(1, F.ring)));
  if (s.tick % step !== 0) return;
  for (let k = 0; k < 4; k++) {
    const want = w.gate[k] ? 0 : F.ring;
    const q = w.quarter[k] as number;
    if (q < want) w.quarter[k] = q + 1;
    else if (q > want) w.quarter[k] = q - 1;
  }
};

/** Whether Varo's knights still hold their gate. */
const held = (s: BattleState): boolean => !!(npcOf(s, 'orderKnight')?.hp || npcOf(s, 'orderKnight2')?.hp);

/** A lurking place: on the cistern's floor on the far side of the altar from Marit (within a quarter turn either way), by the seed. */
const lurk = (s: BattleState, w: SeventhState): void => {
  const m = npcOf(s, 'marit');
  const away = m && (m.x !== 0 || m.y !== 0) ? iatan2(-m.y, -m.x) : rngInt(s.rng, 1024);
  const a = away + rngRange(s.rng, -256, 256);
  const d = rngRange(s.rng, 3400, 5400);
  w.tx = idiv(cosA(a) * d, 10000);
  w.ty = idiv(sinA(a) * d, 10000);
};

/** Far enough from Marit for a run (a line worth standing on). */
const RUN_FROM = 3000;

/** Her slash: the hero near, then whoever of the people stands at her. */
const slash = (s: BattleState, F: NonNullable<SeventhRules['fled']>, w: SeventhState, e: Enemy): void => {
  if (s.tick % F.slash.every !== 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, e.x, e.y, F.slash.reach));
  if (h) {
    e.facing = iatan2(h.y - e.y, h.x - e.x);
    e.anim = 'attack';
    e.animT = 0;
    damageHero(s, h, mulBp(h.stats.maxHp, F.slash.bp), e, 'physical');
    return;
  }
  const st = s.senda;
  const rr = s.rules?.senda;
  if (!st || !rr) return;
  for (let i = 0; i < st.npcs.length; i++) {
    const n = st.npcs[i] as SendaNpc;
    if (n.hp <= 0 || n.away || rr.npcs[i]?.safe || n.id === 'marit' || !withinRange(n.x, n.y, e.x, e.y, F.slash.reach)) continue;
    n.hp--;
    e.anim = 'attack';
    e.animT = 0;
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    return;
  }
};

/** The run resolved: on the hero, on Varo once, else on Marit; where she stops. */
const run = (s: BattleState, F: NonNullable<SeventhRules['fled']>, w: SeventhState, e: Enemy, cue: (c: string) => void): Pt => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  const marit = npcOf(s, 'marit');
  const varo = npcOf(s, 'varo');
  const end = { x: w.line[2] as number, y: w.line[3] as number };
  let stop = end;
  const mine = !!h && sevOnLine(F, h, w.line);
  if (h && mine) {
    damageHero(s, h, mulBp(h.stats.maxHp, F.chargeBp), e, 'physical', true);
    const { t } = segDist2(h.x, h.y, w.line);
    stop = { x: (w.line[0] as number) + idiv(((w.line[2] as number) - (w.line[0] as number)) * imax(0, t - 60), 1000), y: (w.line[1] as number) + idiv(((w.line[3] as number) - (w.line[1] as number)) * imax(0, t - 60), 1000) };
    w.covered++;
    // Caught on the hero's shield: she shows there while she lingers.
    w.caught = s.tick + F.dash + F.linger;
    ev(s, { t: 'senda', kind: 'run', npc: '', x: h.x, y: h.y, n: 1 });
    cue('covered');
  } else if (w.phase === 2 && w.varo === 1 && varo && varo.hp > 0) {
    // Varo, once: into the line meant for Marit.
    w.varo = 2;
    varo.hp = imax(1, varo.hp - 4);
    stop = { x: varo.x, y: varo.y };
    ev(s, { t: 'senda', kind: 'varo', npc: 'varo', x: varo.x, y: varo.y, n: 1 });
    cue('varo');
  } else if (marit && marit.hp > 0) {
    hurt(s, w, marit, F.maritBp);
    w.struck++;
    ev(s, { t: 'senda', kind: 'run', npc: 'marit', x: marit.x, y: marit.y, n: 0 });
    cue('struck');
  }
  // The reflection's run (the last third): on the hero if on its line (and not on hers), else on Marit.
  if (w.echo.length === 4) {
    if (h && !mine && sevOnLine(F, h, w.echo)) {
      damageHero(s, h, mulBp(h.stats.maxHp, mulBp(F.chargeBp, F.reflectBp)), e, 'physical', true);
      ev(s, { t: 'senda', kind: 'run', npc: '', x: h.x, y: h.y, n: 3 });
    } else if (marit && marit.hp > 0) {
      hurt(s, w, marit, mulBp(F.maritBp, F.reflectBp));
      ev(s, { t: 'senda', kind: 'run', npc: 'marit', x: marit.x, y: marit.y, n: 2 });
    }
  }
  return stop;
};

/** Her place on the board each tick (from its state). */
const bossStep = (s: BattleState, F: NonNullable<SeventhRules['fled']>, w: SeventhState, e: Enemy, cue: (c: string) => void): void => {
  const marit = npcOf(s, 'marit');
  const target = marit && marit.hp > 0 ? marit : null;
  if (w.act === 4) {
    // Still: «No voy a…».
    e.anim = 'idle';
    if (s.tick >= w.until) {
      w.act = 0;
      lurk(s, w);
    }
    return;
  }
  if (w.act === 1) {
    e.anim = 'idle';
    if (s.tick < w.until) return;
    // The run.
    const stop = run(s, F, w, e, cue);
    w.act = 2;
    w.until = s.tick + F.dash;
    w.tx = stop.x;
    w.ty = stop.y;
    w.line = [e.x, e.y, stop.x, stop.y];
    return;
  }
  if (w.act === 2) {
    e.anim = 'walk';
    const left = imax(1, w.until - s.tick);
    e.x += idiv(w.tx - e.x, left);
    e.y += idiv(w.ty - e.y, left);
    if (s.tick >= w.until) {
      e.x = w.tx;
      e.y = w.ty;
      w.act = 3;
      w.until = s.tick + F.linger;
      w.line = [];
      w.echo = [];
    }
    return;
  }
  if (w.act === 3) {
    slash(s, F, w, e);
    if (s.tick >= w.until) {
      w.act = 0;
      lurk(s, w);
    }
    return;
  }
  // Lurking: to her place, or to a gate shut while she hides; a line when the time comes.
  let gate = -1;
  for (let g = 0; g < 4; g++) if (w.reopen[g] && (gate < 0 || (w.reopen[g] as number) < (w.reopen[gate] as number))) gate = g;
  if (w.phase === 1 && w.oath < F.oath && gate < 0 && held(s) && w.gate[F.held] === 0) gate = F.held;
  const ready = !!target && s.tick >= w.next && (!withinRange(e.x, e.y, target.x, target.y, RUN_FROM) || (e.x === w.tx && e.y === w.ty));
  if (target && ready) {
    const from = { x: e.x, y: e.y };
    w.line = lineTo(from, target);
    w.act = 1;
    w.until = s.tick + F.warn;
    w.next = s.tick + (F.every[w.phase] as number);
    ev(s, { t: 'senda', kind: 'line', npc: '', x: w.line[2] as number, y: w.line[3] as number, n: 1 });
    cue('line');
    // The last third: her reflection runs too, from the water on another side.
    w.echo = [];
    if (w.phase === 2) {
      const r0 = radiusOf(e.x, e.y);
      const a = iatan2(e.y, e.x) + (s.tick & 1 ? 341 : -341);
      for (const d of [r0, 3200, 2600]) {
        const ox = idiv(cosA(a) * d, 10000);
        const oy = idiv(sinA(a) * d, 10000);
        if (!sevWet(s.rules?.senda?.seventh as SeventhRules, w, ox, oy)) continue;
        w.echo = lineTo({ x: ox, y: oy }, target);
        ev(s, { t: 'senda', kind: 'line', npc: '', x: w.echo[2] as number, y: w.echo[3] as number, n: 2 });
        cue('echo');
        break;
      }
    }
    return;
  }
  slash(s, F, w, e);
  const to = gate >= 0 ? (s.rules?.senda?.seventh?.well?.gates[gate] as Pt) : { x: w.tx, y: w.ty };
  if (gate >= 0 && w.reopen[gate] && s.tick >= (w.reopen[gate] as number) - F.gate.warn && s.tick < (w.reopen[gate] as number) - F.gate.warn + 1) ev(s, { t: 'senda', kind: 'shake', npc: '', x: to.x, y: to.y, n: gate });
  // At a gate: struck open (the held one only when its knights are down; she strikes at them meanwhile).
  if (gate >= 0 && withinRange(e.x, e.y, to.x, to.y, 900)) {
    e.anim = 'idle';
    if (gate === F.held && held(s)) return;
    if (!w.reopen[gate] || s.tick >= (w.reopen[gate] as number)) {
      w.gate[gate] = 1;
      w.reopen[gate] = 0;
      ev(s, { t: 'senda', kind: 'gate', npc: '', x: to.x, y: to.y, n: gate * 2 + 1 });
      cue('struckOpen');
    }
    return;
  }
  e.anim = 'walk';
  stepToward(e, to.x, to.y, F.pace);
};

const fledTick = (s: BattleState, r: SeventhRules, w: SeventhState, cue: (c: string) => void): void => {
  const F = r.fled as NonNullable<SeventhRules['fled']>;
  const st = s.senda;
  if (!st) return;
  if (!w.boss) {
    const e = spawnEnemy(s, 'drowned', 1, false, 0, 0, -4300, F.hp);
    e.facing = 256;
    e.anim = 'idle';
    w.boss = e.id;
    s.bossId = e.id;
    w.tx = 0;
    w.ty = -4300;
    w.next = s.tick + F.warn + 24;
    w.hideAt = s.tick + F.hide.every;
    for (const n of st.npcs) if (n.id === 'varo') n.away = true;
  }
  const e = findEnemy(s, w.boss);
  waterTick(s, F, w);
  // The oath's bar.
  const h = s.heroes.find((x) => x.downTicks === 0);
  const sworn = w.oath >= F.oath;
  if (!sworn) {
    if (w.fell >= 0) w.oath = imin(F.oath, w.oath + imax(1, idiv(F.oath, F.calm)));
    else if (F.together || (h && withinRange(h.x, h.y, F.altar.x, F.altar.y, F.near))) w.oath++;
    if (w.oath >= F.oath) {
      ev(s, { t: 'senda', kind: 'sworn', npc: 'marit', x: F.altar.x, y: F.altar.y });
      cue('sworn');
      w.hidden = 0;
      if (e && e.hp > 0) e.hp -= idiv(e.hp, 2);
      if (w.fell >= 0) w.done = true;
    }
  }
  if (!e || e.hp <= 0) {
    if (w.fell < 0) {
      w.fell = s.tick;
      w.hidden = 0;
      w.line = [];
      w.echo = [];
      ev(s, { t: 'senda', kind: 'down', npc: 'runaway', x: e?.x ?? 0, y: e?.y ?? 0 });
      cue('fell');
      // Her squires go with her.
      for (const id of w.shieldId) {
        const q = findEnemy(s, id);
        if (q && q.hp > 0) q.hp = 0;
      }
      if (w.oath >= F.oath) w.done = true;
    }
    return;
  }
  // The phases, by her life.
  const life = lifeBp(e);
  const phase = life > 6600 ? 0 : life > F.varoAt ? 1 : 2;
  if (phase > w.phase) {
    w.phase = phase;
    if (phase === 1) {
      // «No voy a volver»: the gates struck open (but the knights'), the ring gone, and she hidden.
      for (let g = 0; g < 4; g++) {
        if (g === F.held && held(s)) continue;
        w.gate[g] = 1;
        const p = (r.well as NonNullable<SeventhRules['well']>).gates[g] as Pt;
        ev(s, { t: 'senda', kind: 'gate', npc: '', x: p.x, y: p.y, n: g * 2 + 1 });
      }
      w.shieldAt = s.waveT + F.shield.first;
      w.folkSalt = s.tick + F.salt.folk;
      ev(s, { t: 'senda', kind: 'hide', npc: '', x: e.x, y: e.y, n: 1 });
      cue('hide');
    } else {
      // She won't go: no more hiding. Varo comes down.
      w.hidden = 0;
      w.hideAt = s.tick + F.still.every;
      w.reopen = [0, 0, 0, 0];
      const varo = npcOf(s, 'varo');
      if (varo) varo.away = false;
      ev(s, { t: 'senda', kind: 'varo', npc: 'varo', x: F.varoOut.x, y: F.varoOut.y, n: 0 });
      cue('stay');
    }
  }
  // Hiding: now and then (the first third), always (the second), trying and stilled (the last).
  if (!sworn) {
    if (w.phase === 0 && s.tick >= w.hideAt) {
      w.hideAt = s.tick + F.hide.every;
      w.hidden = s.tick + F.hide.ticks;
      ev(s, { t: 'senda', kind: 'hide', npc: '', x: e.x, y: e.y, n: 0 });
      cue('gone');
    } else if (w.phase === 1) w.hidden = NEVER;
    else if (w.phase === 2 && s.tick >= w.hideAt && w.act === 0) {
      w.hideAt = s.tick + F.still.every;
      w.act = 4;
      w.until = s.tick + F.still.ticks;
      ev(s, { t: 'senda', kind: 'still', npc: '', x: e.x, y: e.y });
      cue('still');
    }
  }
  if (w.phase === 2 || sworn) w.hidden = 0;
  bossStep(s, F, w, e, cue);
  // Where she walks in the water or on salt, it shows: ripples, prints.
  if (s.tick % 6 === 0 && (sevWet(r, w, e.x, e.y) || onSalt(F, w, e.x, e.y) >= 0)) {
    w.marks.push(e.x, e.y, s.tick);
    if (w.marks.length > 180) w.marks.splice(0, w.marks.length - 180);
  }
  // The salt: Marit's (if the hero swears) where she is; the salt folk's where she hides, in the second phase.
  const marit = npcOf(s, 'marit');
  const nearest = (x: number, y: number): number => {
    let k = -1;
    let bd = 0x7fffffff;
    for (let i = 0; i < F.runnels.length; i++) {
      if (w.salt[i]) continue;
      const d = segDist2(x, y, F.runnels[i] as number[]).d2;
      if (d < bd) {
        bd = d;
        k = i;
      }
    }
    return k;
  };
  const salt = (k: number, by: string): void => {
    if (k < 0) return;
    const seg = F.runnels[k] as number[];
    w.salt[k] = s.tick;
    ev(s, { t: 'senda', kind: 'salt', npc: by, x: idiv((seg[0] as number) + (seg[2] as number), 2), y: idiv((seg[1] as number) + (seg[3] as number), 2), n: k });
  };
  if (!F.together && marit && marit.hp > 0) {
    if (!w.maritSalt) w.maritSalt = s.tick + F.salt.every;
    if (s.tick >= w.maritSalt) {
      w.maritSalt = s.tick + F.salt.every;
      salt(nearest(marit.x, marit.y), 'marit');
    }
  }
  if (w.phase === 1 && w.folkSalt && s.tick >= w.folkSalt) {
    w.folkSalt = s.tick + F.salt.folk;
    const folk = npcOf(s, 'salinera')?.hp || npcOf(s, 'salter')?.hp;
    if (folk) salt(nearest(e.x, e.y), 'salinera');
  }
  // The squires again while she hides.
  shieldsTick(s, r, w, w.phase === 1 && !sworn, cue);
  // The people at their places: Marit kneels (sworn together) or fights four hexes off; Varo, down, beside her.
  if (marit && !F.together) {
    const i = st.npcs.indexOf(marit);
    w.swing[i] = guardTick(s, marit, keepOf(r, 'marit') as Pt, r.marit, w.swing[i] ?? 0, (x) => !sevSeen(s, x));
  }
  const varo = npcOf(s, 'varo');
  if (varo && !varo.away && varo.hp > 0) {
    const i = st.npcs.indexOf(varo);
    if (w.varo === 0) {
      varo.moving = !stepToward(varo, F.varoIn.x, F.varoIn.y, 80);
      if (!varo.moving) w.varo = 1;
    } else w.swing[i] = guardTick(s, varo, F.varoIn, r.marit, w.swing[i] ?? 0, (x) => !sevSeen(s, x) || x.id === w.boss);
  }
};

/** La Que Huyó and the squires move by the act's own rules (true); nothing else moves them. */
export const seventhHold = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.seventh;
  const w = s.senda?.seventh;
  if (!r || !w) return false;
  if (e.id === w.boss) return true;
  const i = find(w.shieldId, e.id);
  if (i >= 0) {
    if (e.stun > 0) return false;
    squireMove(s, r, w, e, i);
    return true;
  }
  return false;
};

export const seventhTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.seventh;
  const st = s.senda;
  if (!r || !st) return;
  if (!st.seventh) st.seventh = seventhFresh(r, st.npcs.length);
  const w = st.seventh;
  if (!s.started) return;
  work(s, r, w, cue);
  if (r.kind === 'rubble') rubbleTick(s, r, w, cue);
  else if (r.kind === 'well') wellTick(s, r, w, cue);
  else fledTick(s, r, w, cue);
};

/** The rubble: heaps cleared; the well: the marks down to bare; the fight: her life taken (its half leaves a scar). */
export const seventhGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.seventh;
  const w = s.senda?.seventh;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.rubble) return { done: sevCleared(r, w), need: r.rubble.piles.length };
  if (r.kind === 'well') {
    const W = r.well as NonNullable<SeventhRules['well']>;
    return { done: W.cap - imax(W.bare, w.level), need: W.cap - W.bare };
  }
  const e = findEnemy(s, w.boss);
  if (!e) return { done: w.fell >= 0 ? 1 : 0, need: 1 };
  return { done: e.maxHp - imax(0, e.hp), need: e.maxHp };
};

export const seventhResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.seventh;
  const w = s.senda?.seventh;
  if (!r || !w) return null;
  if (r.rubble) return w.doneAt >= 0 && s.tick >= w.doneAt + r.rubble.settle ? 'won' : null;
  return w.done ? 'won' : null;
};
